import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import useAsync from 'hooks/useAsync';
import useCurrentUser from 'hooks/useCurrentUser';
import { login } from 'actions/users';
import { Link, useSearchParams } from 'react-router-dom';
import { TextLink } from '@teachstonellc/component-library';
import { useCallback, useEffect, useState } from 'react';
import PhonePrompt from 'components/Users/PhonePrompt';
import { useNavigate } from 'react-router-dom';
import {
  redirectAfterAuth,
  handleAuthHydrationFailure
} from 'helpers/authRedirect';
import ContainerWithLogo from 'components/ContainerWithLogo';
import { useDispatch } from 'react-redux';

import LoginManager, {
  LoginManagerProps,
  setLoginError
} from 'components/LoginManager';
import usePasswordExpiredRedirect from 'hooks/usePasswordExpiredRedirect';

import {
  Header,
  PageContainer,
  ProductWrapper,
  ProductLabel,
  PasswordHeader,
  PasswordBody
} from './Styled';

type LoginSteps = 'login' | 'setupPhone';

function Login() {
  const { run } = useAsync();
  const [user, setUser] = useState();
  const [step, setStep] = useState<LoginSteps>('login');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const [query] = useSearchParams();
  const skipOnboarding = query.get('skip_onboarding') === 'true';
  const redirectIfExpired = usePasswordExpiredRedirect();

  useEffect(() => {
    if (currentUser.id && step === 'login') {
      navigate(RouteHelpers.getPath('app'), { replace: true });
    }

    setIsLoading(false);
    // Only check "already logged in" once, on mount. This must NOT re-run
    // when currentUser.id changes later - finishLogin's own post-login
    // dispatch(update(...)) also flips currentUser.id, which would re-fire
    // this effect and unconditionally navigate to the dashboard, racing
    // (and overriding) finishLogin's own destination-aware redirect_to/
    // abs_redirect/return_to navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishLogin = useCallback(
    async (user: any) => {
      try {
        await redirectAfterAuth(
          user,
          {
            redirectTo: query.get('redirect_to'),
            absRedirect: query.get('abs_redirect')
          },
          { dispatch, navigate }
        );
      } catch {
        handleAuthHydrationFailure(i18n.ft(messages.errors.generic));
      }
    },
    // Optimize render by only creating callback once.
    // None of the suggested dependencies affect this (history, query).
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [dispatch]
  );

  const handleSetPhone = useCallback(() => {
    finishLogin(user);
  }, [user, finishLogin]);

  const handleLogin: LoginManagerProps['onLogin'] = (
    values,
    formik,
    isFormWithPassword
  ) => {
    run(login(values), {
      onSuccess: (user: any) => {
        if (user.login_phone_prompt && !skipOnboarding) {
          setUser(user);
          setStep('setupPhone');
        } else {
          finishLogin(user);
        }
      },
      onError: (error: any) => {
        setLoginError(error, formik, isFormWithPassword, values);
        formik.setSubmitting(false);
        redirectIfExpired(error, values.user.email, values.user.password);
      }
    });
  };

  // This value is set by myCoach Connect mobile app when embedding this
  // page inside myCoach Connect React Native application.
  const isReactNative = (window as any).isReactNative;

  if (isLoading) {
    return null;
  }

  return (
    <PageContainer>
      <ContainerWithLogo>
        {step === 'login' && (
          <LoginManager
            onLogin={handleLogin}
            renderTop={loginStep => (
              <div>
                {loginStep === 'login' && !isReactNative && (
                  <ProductWrapper>
                    <ProductLabel>
                      {i18n.ft(messages.productKey.label)}
                    </ProductLabel>
                    <TextLink
                      as={Link}
                      to={RouteHelpers.getPath('users-activate-pk')}
                      className="[&>span]:text-ds-text-xs [&>span]:font-[proxima-nova]"
                    >
                      {i18n.ft(messages.productKey.button)}
                    </TextLink>
                  </ProductWrapper>
                )}

                <Header>{i18n.ft(messages.login)}</Header>
              </div>
            )}
            renderBottom={loginStep =>
              loginStep === 'login' && !isReactNative ? (
                <div>
                  <PasswordHeader>
                    {i18n.ft(messages.passwordHeader)}
                  </PasswordHeader>
                  <PasswordBody>
                    <span>{i18n.ft(messages.password01)}</span>
                    <TextLink
                      as={Link}
                      to={RouteHelpers.getPath('users-resend-email')}
                      className="[&>span]:text-[1rem] [&>span]:font-[proxima-nova]"
                    >
                      {i18n.ft(messages.password02)}
                    </TextLink>
                    <span>{i18n.ft(messages.password03)}</span>
                  </PasswordBody>
                </div>
              ) : null
            }
          />
        )}

        {step === 'setupPhone' && (
          <PhonePrompt onSuccess={handleSetPhone} headingAs="h1" />
        )}
      </ContainerWithLogo>
    </PageContainer>
  );
}

export default Login;
